/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Card,
  Tabs,
  message,
} from 'antd';
import {
  EnvironmentOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import Direccion from 'components/Direccion';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import makeMessager from 'utils/complexFormsMessages';
import { makeGetOnFinish, makeOnChangeTabKey } from 'utils/tabsManagement';
import {
  deleteYarda,
  getYarda,
  getYardas,
  patchYarda,
  postYarda,
} from 'api/catalogos/yardas';
import { ActionsNames, getModelPermission, hasActionPermission } from 'utils/permission/index';

const { TabPane } = Tabs;
const baseURI = 'catalogos/yardas/';

const models = ['yarda'];

const Yardas = ({ permission }) => {
  const addressPermission = useMemo(() => getModelPermission('direccion'), []);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [formAddress, setFormAddress] = useState();
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);

  const basicKeys = ['general', 'info'];
  const complexForms = [
    { key: 'direccion', formInstance: formAddress, permission: addressPermission }];

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    setCurrentPage(page);
    const _data = await getYardas({
      ...q,
      page,
    });
    setData(_data);
    setLoading(false);
  };

  const showMsg = makeMessager(setCurrentTabKey, fetchData);

  useEffect(() => {
    let mounted = true;
    let _hasAllPermission = true;
    const validatePermission = () => {
      const _hasAllViewPermission = models
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        _hasAllPermission = _hasAllViewPermission;
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const [_data] = await Promise.all([
        getYardas({ page: 1 }, true),
      ]);
      if (mounted) {
        setData(_data);
        setLoading(false);
      }
    };
    validatePermission();
    if (_hasAllPermission) {
      fetchAll();
    }
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setCurrentTabKey('general');
  };

  const onClickEdit = async (id) => {
    const key = selectedRowKeys[0] || id || id?.id;
    const match = await getYarda(key);
    if (match) {
      form.setFieldsValue(match);
    }
    setVisible(true);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      const _data = await (key ? patchYarda(key, values) : postYarda(values));
      if (_data) {
        await fetchData();
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([_data.id]);
          }
          await onClickEdit(_data.id);
        } else {
          onCancel();
        }
        setLoading(false);
        return _data;
      }
      setLoading(false);
      return true;
    } catch (err) {
      onError(err, setLoading, [form], setCurrentTabKey);
      return false;
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteYarda(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
    },
  ];

  const rules = {
    required: [
      { required: true },
    ],
  };

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys, complexForms, currentTabKey, setCurrentTabKey, showMsg,
  );

  const getOnFinish = makeGetOnFinish(basicKeys, complexForms, currentTabKey, onFinish);

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible
          ? (
            <Table
              cols={columns}
              mobileColumns={[columns[0], columns[1]]}
              data={data.results || []}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              controls={{
                onClickAdd,
                onClickEdit,
                onClickDelete,
              }}
              rowKey="id"
              baseURI={baseURI}
              permission={permission}
              pagination={false}
              paginationCallback={fetchData}
              count={data.count}
              currentPage={currentPage}
              hasAllViewPermission={hasAllViewPermission}
            />
          ) : (
            <Col span={24} style={{ height: '100%' }}>
              <Card
                className="form-container"
                title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Yarda`}
                extra={(
                  <FormSubmitControls
                    onFinish={getOnFinish()}
                    onCancel={onCancel}
                    allowSaveAndContinue
                    onCancelText="Cancelar"
                    permission={permission}
                  />
                )}
                bordered={false}
              >
                <Tabs
                  onChange={onChangeTabKey}
                  activeKey={currentTabKey}
                  type="card"
                >
                  <TabPane
                    tab={(
                      <span>
                        <InfoCircleOutlined />
                        Información General
                      </span>
                    )}
                    key="general"
                    forceRender
                  >
                    <Form
                      name="general"
                      layout="vertical"
                      form={form}
                      onFinish={() => onFinish()}
                      scrollToFirstError
                    >
                      <Row gutter={10}>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="clave"
                            label="clave"
                            rules={rules.required}
                            hasFeedback
                            required
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="descripcion"
                            rules={rules.required}
                            label="Descripción"
                            hasFeedback
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="es_proveedor_externo"
                            rules={rules.required}
                            label="¿Es proveedor externo?"
                            hasFeedback
                          >
                            <Select trueFalse />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={16}>
                          <Form.Item
                            name="observaciones"
                            label="Observaciones"
                            hasFeedback
                          >
                            <Input.TextArea autoSize={{ minRows: 3, maxRows: 3 }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item hidden>
                        <Button htmlType="submit" />
                      </Form.Item>
                    </Form>
                  </TabPane>
                  {addressPermission?.view && (
                    <TabPane
                      tab={(
                        <>
                          <EnvironmentOutlined />
                          Dirección
                        </>
                      )}
                      key="direccion"
                      forceRender
                      disabled={!selectedRowKeys.length}
                    >
                      <Direccion
                        currentURI={baseURI}
                        parentID={selectedRowKeys[0]}
                        setLoading={setLoading}
                        setFormAddress={setFormAddress}
                        setCurrentTabKey={setCurrentTabKey}
                        addressPropName="direccion"
                        callback={(values, _continue) => {
                          if (!_continue) {
                            onCancel();
                          } else {
                            const [key] = selectedRowKeys;
                            const clone = [...data];
                            const mappedData = clone.map((e) => (e.id === key
                              ? ({ ...e, direccion: values?.id }) : e));
                            setData(mappedData);
                          }
                        }}
                        permission={addressPermission}
                      />
                    </TabPane>
                  )}
                </Tabs>
              </Card>
            </Col>
          )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          visible={visibleAlert}
          content="Yarda seleccionada"
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default Yardas;
